import { AuthProvider, Descope, useDescope, useSession, useUser } from '@descope/react-sdk'
import { registerLocale } from 'i18n-iso-countries'
import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import ms from 'ms'
import { Suspense, lazy, useCallback, useEffect } from 'react'
import { Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom'

import { useTrackUser } from '@/lib/analytics/track-user.hook'
import { AIGovernanceModuleRoute } from '@/lib/app/ai-governance-module-route'
import { DiscoveryModuleRoute } from '@/lib/app/discovery-module-route'
import { OutreachCampaignsModuleRoute } from '@/lib/app/outreach-campaigns-module-route'
import { ProtectedRoute } from '@/lib/app/protected-route'
import { useActiveOrgGuard } from '@/lib/auth/active-org-guard.hook'
import { OrgDetailsProvider, useOrgIdFromToken } from '@/lib/auth/orgid.hook'
import { RecipientQuestionnaireTransportProvider } from '@/lib/connect/connect-transport'
import { AppRoute } from '@/lib/url/urls'

import { SingleAssessment } from '@/pages/assessment/single-assessment'
import '@/pages/company-drawer/antd-drawer.less'
import { portalLoginFlag } from '@/pages/questionnaire-v2/portal-login-flag/portal-login-flag'

import { AuthenticatedLayout } from '@/components/layouts/authenticated-layout'
import { AppBanners } from '@/components/shared/app-banners/app-banners'
import { ErrorBoundary } from '@/components/shared/error-boundary/error-boundary'

import { ConfirmModalProvider } from './components/lemmala-design-system/modal/confirm-modal-provider'
import { toast } from './components/lemmala-design-system/toast/use-toast'

import enLocale from 'i18n-iso-countries/langs/en.json'

registerLocale(enLocale)

const PreviewQuestionnaire = lazy(() =>
  import('@/pages/questionnaire-v2/preview-questionnaire/preview-questionnaire').then((module) => ({
    default: module.PreviewQuestionnaire,
  })),
)

const AlertInventory = lazy(() =>
  import('@/pages/alert-inventory/alert-inventory').then((module) => ({
    default: module.AlertInventory,
  })),
)
const SignalFeed = lazy(() =>
  import('@/pages/alert-inventory/signal-feed').then((module) => ({
    default: module.SignalFeed,
  })),
)
const RequestsPage = lazy(() =>
  import('@/pages/requests-page/requests-page').then((module) => ({
    default: module.RequestsPage,
  })),
)
const PastAssessment = lazy(() =>
  import('@/pages/past-assessment/past-assessment-report/past-assessment').then((module) => ({
    default: module.PastAssessment,
  })),
)
const QuestionnaireTemplate = lazy(() =>
  import('@/pages/questionnaire-template/v1/questionnaire-template').then((module) => ({
    default: module.QuestionnaireTemplate,
  })),
)
const AssessorQuestionnaire = lazy(() =>
  import('@/pages/questionnaire/assessor-questionnaire/assessor-questionnaire').then((module) => ({
    default: module.AssessorQuestionnaire,
  })),
)

const RecipientQuestionnairesv2 = lazy(() =>
  import('@/pages/questionnaire-v2/recipient-questionnaires/recipient-questionnaires').then(
    (module) => ({
      default: module.RecipientQuestionnaires,
    }),
  ),
)

const CompanyRequestForm = lazy(() =>
  import('@/pages/company-request/company-request-form').then((module) => ({
    default: module.CompanyRequestForm,
  })),
)

const DocsLoginPage = lazy(() =>
  import('@/pages/docs-login/docs-login-page').then((module) => ({
    default: module.DocsLoginPage,
  })),
)

const DocsSSOPage = lazy(() =>
  import('@/pages/docs-sso/docs-sso-page').then((module) => ({
    default: module.DocsSSOPage,
  })),
)

const LoginPage = lazy(() =>
  import('@/pages/login-page').then((module) => ({ default: module.LoginPage })),
)
const DashboardsPage = lazy(() =>
  import('@/pages/dashboards/dashboards-list/dashboards-page').then((module) => ({
    default: module.DashboardsPage,
  })),
)
const DashboardViewPage = lazy(() =>
  import('@/pages/dashboards/dashboard-view/dashboard-view-page').then((module) => ({
    default: module.DashboardViewPage,
  })),
)
const DashboardEditorPage = lazy(() =>
  import('@/pages/dashboards/dashboard-editor/dashboard-editor-page').then((module) => ({
    default: module.DashboardEditorPage,
  })),
)
const IntegrationSetupPage = lazy(() =>
  import('@/pages/settings-page/integration-section/integration-setup/integration-setup').then(
    (module) => ({
      default: module.IntegrationSetupPage,
    }),
  ),
)
const SettingsPage = lazy(() =>
  import('@/pages/settings-page/settings-page').then((module) => ({
    default: module.SettingsPage,
  })),
)
const SettingsHubPage = lazy(() =>
  import('@/pages/settings-page/settings-hub-page').then((module) => ({
    default: module.SettingsHubPage,
  })),
)
const SettingsSectionRoute = lazy(() =>
  import('@/pages/settings-page/settings-section-route').then((module) => ({
    default: module.SettingsSectionRoute,
  })),
)
const ThirdPartyInventoryPage = lazy(() =>
  import('@/pages/third-party-inventory/third-party-page').then((module) => ({
    default: module.ThirdPartyInventoryPage,
  })),
)
const ThirdPartyPage = lazy(() =>
  import('@/pages/third-party-page/third-party-page').then((module) => ({
    default: module.ThirdPartyPage,
  })),
)
const FourthPartiesPage = lazy(() =>
  import('@/pages/third-party-inventory/fourth-parties-page').then((module) => ({
    default: module.FourthPartiesPage,
  })),
)
const DiscoveredPage = lazy(() =>
  import('@/pages/discovered/discovered-page').then((module) => ({
    default: module.DiscoveredPage,
  })),
)
const StorybookPage = lazy(() =>
  import('@/pages/storybook/storybook-page').then((module) => ({
    default: module.StorybookPage,
  })),
)

const AssessmentsPage = lazy(() =>
  import('@/pages/assessment/assessment-page').then((module) => ({
    default: module.AssessmentsPage,
  })),
)

const OverviewPage = lazy(() =>
  import('@/pages/overview/overview-page').then((module) => ({
    default: module.OverviewPage,
  })),
)

const OutreachPage = lazy(() =>
  import('@/pages/outreach/outreach-page').then((module) => ({ default: module.OutreachPage })),
)

const NewOutreachCampaignPage = lazy(() =>
  import('@/pages/outreach/campaign/new-campaign-page').then((module) => ({
    default: module.NewCampaignPage,
  })),
)

const OutreachCampaignPage = lazy(() =>
  import('@/pages/outreach/campaign/campaign-page').then((module) => ({
    default: module.CampaignPage,
  })),
)

const AiGovernancePage = lazy(() =>
  import('@/pages/ai-governance/ai-governance-page').then((module) => ({
    default: module.AiGovernancePage,
  })),
)

const descopeProjectID = import.meta.env.VITE_DESCOPE_PROJECTID
const descopeBaseUrl = import.meta.env.VITE_DESCOPE_BASE_URL
const env = import.meta.env.VITE_CGX_ENV
const suspensifyPage = (page: React.ReactNode) => <Suspense fallback={<></>}>{page}</Suspense>

// Wrapper component to handle portal route with proper portalId extraction
const PortalRouteWrapper = () => {
  const { isAuthenticated } = useSession()

  if (!isAuthenticated) {
    return (
      <div className='mx-auto mt-16 max-w-lg'>
        <Descope
          flowId={'recipient-sign-in'}
          theme='light'
          onSuccess={() => {
            portalLoginFlag.set()
          }}
          onError={() => {
            toast({
              title: 'Error',
              description: 'An error occurred. Please try again later.',
              variant: 'destructive',
            })
          }}
        />
      </div>
    )
  }

  return (
    <RecipientQuestionnaireTransportProvider>
      {suspensifyPage(<RecipientQuestionnairesv2 />)}
    </RecipientQuestionnaireTransportProvider>
  )
}

export const App = () => {
  return (
    <ErrorBoundary>
      <ConfirmModalProvider>
        <div className='min-w-fit'>
          <Routes>
            <Route
              key={'lema-app'}
              element={
                <LemaAppAuthProviders>
                  <LemaAppTrackProvider>
                    <>
                      <AppBanners />
                      <Outlet />
                    </>
                  </LemaAppTrackProvider>
                </LemaAppAuthProviders>
              }
            >
              <Route
                key={'login'}
                path={AppRoute.LOGIN}
                element={suspensifyPage(<LoginPage authenticatedPath={AppRoute.OVERVIEW} />)}
              />
              <Route
                key={'docs-login'}
                path={AppRoute.DOCS_LOGIN}
                element={suspensifyPage(<DocsLoginPage />)}
              />
              <Route
                key={'docs-sso'}
                path={AppRoute.DOCS_SSO}
                element={
                  <ProtectedRoute loginPath={AppRoute.LOGIN}>
                    {/* no navigation bar on this page: it redirects straight to docs.lema.ai */}
                    {suspensifyPage(<DocsSSOPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                key={'preview-smart-questionnaire'}
                path={AppRoute.PREVIEW_QUESTIONNAIRE}
                element={suspensifyPage(<PreviewQuestionnaire />)}
              />
              {env !== 'production' && (
                // show story book without top navigation, only for non-production authed users
                <Route
                  key={'storybook'}
                  path={AppRoute.STORYBOOK}
                  element={
                    <ProtectedRoute requiredPermissions={['base.view']}>
                      {suspensifyPage(<StorybookPage />)}
                    </ProtectedRoute>
                  }
                />
              )}
              <Route
                key={'lema-app-authenticated'}
                element={
                  <ProtectedRoute requiredPermissions={['base.view']}>
                    <AuthenticatedLayout />
                  </ProtectedRoute>
                }
              >
                <Route path={AppRoute.OVERVIEW} element={suspensifyPage(<OverviewPage />)} />
                <Route path={AppRoute.DASHBOARDS} element={suspensifyPage(<DashboardsPage />)} />
                <Route
                  path={AppRoute.DASHBOARD_NEW}
                  element={suspensifyPage(<DashboardEditorPage />)}
                />
                <Route
                  path={AppRoute.DASHBOARD_VIEW}
                  element={suspensifyPage(<DashboardViewPage />)}
                />
                <Route
                  path={AppRoute.DASHBOARD_EDIT}
                  element={suspensifyPage(<DashboardEditorPage />)}
                />
                <Route
                  path={AppRoute.PROCUREMENT_REQUESTS}
                  element={suspensifyPage(<RequestsPage />)}
                />
                <Route path={AppRoute.ASSESSMENTS} element={suspensifyPage(<AssessmentsPage />)} />
                <Route
                  path={AppRoute.THIRD_PARTIES}
                  element={suspensifyPage(<ThirdPartyInventoryPage />)}
                />
                <Route
                  path={AppRoute.THIRD_PARTY}
                  element={
                    <ProtectedRoute requiredPermissions={['base.view']}>
                      {suspensifyPage(<ThirdPartyPage />)}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={AppRoute.OUTREACH}
                  element={
                    <OutreachCampaignsModuleRoute>
                      {suspensifyPage(<OutreachPage />)}
                    </OutreachCampaignsModuleRoute>
                  }
                />
                <Route
                  path={AppRoute.NEW_OUTREACH_CAMPAIGN}
                  element={
                    <OutreachCampaignsModuleRoute>
                      <ProtectedRoute requiredPermissions={['base.manage']}>
                        {suspensifyPage(<NewOutreachCampaignPage />)}
                      </ProtectedRoute>
                    </OutreachCampaignsModuleRoute>
                  }
                />
                <Route
                  path={AppRoute.OUTREACH_CAMPAIGN}
                  element={
                    <OutreachCampaignsModuleRoute>
                      {suspensifyPage(<OutreachCampaignPage />)}
                    </OutreachCampaignsModuleRoute>
                  }
                />
                <Route
                  path={AppRoute.ENGAGEMENTS}
                  element={<Navigate to='/third-parties?inventoryView=engagements' replace />}
                />
                <Route
                  path={AppRoute.FOURTH_PARTIES}
                  element={suspensifyPage(<FourthPartiesPage />)}
                />
                <Route
                  path={AppRoute.DISCOVERED}
                  element={
                    <DiscoveryModuleRoute>
                      {suspensifyPage(<DiscoveredPage />)}
                    </DiscoveryModuleRoute>
                  }
                />
                <Route path={AppRoute.MONITORING} element={suspensifyPage(<AlertInventory />)} />
                <Route path={AppRoute.SIGNAL_FEED} element={suspensifyPage(<SignalFeed />)} />
                <Route
                  path={AppRoute.AI_GOVERNANCE}
                  element={
                    <AIGovernanceModuleRoute>
                      {suspensifyPage(<AiGovernancePage />)}
                    </AIGovernanceModuleRoute>
                  }
                />
                <Route
                  key={'settings'}
                  path={AppRoute.SETTINGS}
                  element={
                    <ProtectedRoute requiredPermissions={['settings.view']}>
                      {suspensifyPage(<SettingsPage />)}
                    </ProtectedRoute>
                  }
                >
                  <Route index element={suspensifyPage(<SettingsHubPage />)} />
                  <Route path=':section' element={suspensifyPage(<SettingsSectionRoute />)} />
                </Route>
                <Route
                  path={AppRoute.ASSESSMENT}
                  element={
                    <ProtectedRoute requiredPermissions={['base.view']}>
                      <SingleAssessment />
                    </ProtectedRoute>
                  }
                />
                <Route
                  key={'past-assessment'}
                  path={AppRoute.PAST_ASSESSMENT}
                  element={
                    <ProtectedRoute requiredPermissions={['base.view']}>
                      {suspensifyPage(<PastAssessment />)}
                    </ProtectedRoute>
                  }
                />
                <Route
                  key={'integrations'}
                  path={AppRoute.INTEGRATION_SETUP}
                  element={
                    <ProtectedRoute requiredPermissions={['integrations.manage']}>
                      {suspensifyPage(<IntegrationSetupPage />)}
                    </ProtectedRoute>
                  }
                />
                <Route
                  key={'questionnaires-room'}
                  path={AppRoute.QUESTIONNAIRE}
                  element={
                    <ProtectedRoute requiredPermissions={['base.view']}>
                      {suspensifyPage(<AssessorQuestionnaire />)}
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route
                key={'template'}
                path={AppRoute.QUESTIONNAIRE_TEMPLATE}
                element={
                  <ProtectedRoute requiredPermissions={['base.view']}>
                    {suspensifyPage(<QuestionnaireTemplate />)}
                  </ProtectedRoute>
                }
              />
              <Route
                key={'requests'}
                path={AppRoute.COMPANY_REQUEST}
                element={
                  <ProtectedRoute>
                    {/* make sure user is logged in before accessing company-request page,
                did not use route `lema-app-authenticated` to avoid including navigation bar */}
                    {suspensifyPage(<CompanyRequestForm />)}
                  </ProtectedRoute>
                }
              />
              <Route
                key={'questionnaire'}
                path={AppRoute.QUESTIONNAIRE_PORTAL}
                element={<PortalRouteWrapper />}
              />
            </Route>
            {/* Catch-all route - redirect all unmatched routes to /overview */}
            <Route path='*' element={<Navigate to={AppRoute.OVERVIEW} replace />} />
          </Routes>
        </div>
      </ConfirmModalProvider>
    </ErrorBoundary>
  )
}

const TokenExpiryMonitor = () => {
  // Periodically check if the refresh token is close to expiring and log out if it is - so it is refreshed
  const descope = useDescope()
  const navigate = useNavigate()
  const refreshTokenExpirationCookieName = 'x-refresh-token-expiration'

  const checkTokenExpiry = useCallback(async () => {
    const refreshToken = descope.getRefreshToken()

    let expTimeSeconds = 0

    // get the refresh token from the descope (local storage -> development setup)
    if (refreshToken) {
      const decodedToken = jwtDecode(refreshToken)

      expTimeSeconds = decodedToken.exp || 0
    } else {
      // get the refresh token from our custom cookie (refresh is not in local storage -> production setup)
      const expirationTime = Cookies.get(refreshTokenExpirationCookieName)

      if (expirationTime) {
        expTimeSeconds = Math.floor(new Date(expirationTime).getTime() / 1000)
      }
    }

    if (!expTimeSeconds) return

    const currentTime = Math.floor(Date.now() / 1000)
    const timeUntilExpirySeconds = expTimeSeconds - currentTime

    if (timeUntilExpirySeconds <= ms('30m') / 1000) {
      // Delete the cookie so we don't go into a loop when loading the login page
      Cookies.remove(refreshTokenExpirationCookieName)
      await descope.logout(refreshToken)
      navigate(AppRoute.LOGIN)
    }
  }, [descope, navigate])

  useEffect(() => {
    checkTokenExpiry()

    const intervalId = setInterval(checkTokenExpiry, ms('5m')) // Every 5 minutes
    return () => clearInterval(intervalId)
  }, [checkTokenExpiry])

  return null
}

const LemaAppAuthProviders = ({ children }: { children: React.ReactElement }) => {
  return (
    <AuthProvider projectId={descopeProjectID} baseUrl={descopeBaseUrl}>
      <TokenExpiryMonitor />
      <OrgDetailsProvider>{children}</OrgDetailsProvider>
    </AuthProvider>
  )
}

const LemaAppTrackProvider = ({ children }: { children: React.ReactElement }) => {
  const { user } = useUser()
  const { sessionToken } = useSession()
  const orgInfo = useOrgIdFromToken(user, sessionToken)
  useTrackUser(user, orgInfo?.orgId || '')
  // Reload this tab if the active org is switched in another tab, so its UI and requests stay aligned.
  useActiveOrgGuard()

  return children
}
